import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import moment from "moment";
import { Space, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IconPrimary, Popup } from "../../components";
import { CircleTick, CloseIcon } from "../../assets";
import { usePopup } from "../../hooks/usePopup";
import RequestConfirmForm from "../../forms/RequestConfirmForm";
import DenyRequestForm from "../../forms/DenyRequestForm";
import SendQuotationForm from "../../forms/SendQuotation";
import { getCommonState, getRequestState } from "../../redux/store/getState";
import { RequestAtc } from "../../redux/apiSlice/requestSlice";
import modifyId from "../../utils/modifyId";
import useSearch from "../../hooks/useSearch";
import {
  filterUserRequests,
  getRequestsUser,
} from "../../redux/apiActions/requestsAction";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const RequestUserTable: React.FC = () => {
  const popup = usePopup();
  const dispatch = useAppDispatch();
  const { requestUser, loading } = useAppSelector(getRequestState);
  const { filterFrom, filterTo, atc_id } = useAppSelector(getCommonState);
  const [data, setData] = useState<RequestAtc[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [record, setRecord] = useState<RequestAtc>();

  useSearch(requestUser, setData);

  useEffect(() => {
    if (filterFrom && filterTo && atc_id) {
      dispatch(filterUserRequests({ from: filterFrom, to: filterTo, atc_id }));
    } else {
      dispatch(getRequestsUser());
    }
  }, [filterFrom, filterTo, atc_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  const columns: ColumnsType<RequestAtc> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Request ID",
      dataIndex: "id",
      render: (id) => <>{modifyId(id)}</>,
    },
    {
      title: "Username",
      dataIndex: "user",
      render: (user) => <>{user.username}</>,
    },
    {
      title: "Email ID",
      dataIndex: "user",
      render: (user) => <>{user.email}</>,
    },
    {
      title: "Exam Name",
      dataIndex: "exam",
      render: (exam) => <>{exam.name}</>,
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_number",
    },
    {
      title: "Payment ID",
      dataIndex: "payment_id",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <IconPrimary
            onClick={() => {
              setRecord(record);
              popup.open();
            }}
          >
            <CircleTick />
          </IconPrimary>

          <Popup
            openTrigger={
              <IconPrimary>
                <CloseIcon />
              </IconPrimary>
            }
            ref={popup.popupRef}
          >
            <DenyRequestForm record={record} popup={popup} />
          </Popup>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        style={{ marginTop: 20, whiteSpace: "pre" }}
        loading={loading.getRequest}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        onChange={(p) =>
          setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
        }
        pagination={pagination}
        rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
      />
      <Popup openTrigger={<></>} ref={popup.popupRef}>
        {record?.status === "voucher_requested" ? (
          <RequestConfirmForm record={record} popup={popup} />
        ) : record ? (
          <SendQuotationForm record={record} popup={popup} />
        ) : null}
      </Popup>
    </>
  );
};

export default RequestUserTable;
